import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { asId, type ConversationId } from '@cyphex/types';
import { REALTIME_ENABLED, getRealtimeClient } from '@/lib/realtime';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { CHAT_KEYS } from '@/features/chat/constants';
import { POST_KEYS } from '@/features/posts/api/queryKeys';

/**
 * The cold-load path (protocol §9, tier 3).
 *
 * `resync.too_far` or a refused resume means the client's local view of a topic
 * is unusable — it fell outside the replay window. Refetching is not a fallback
 * here, it IS the designed recovery, and it has to be as robust as the happy
 * path because it is also what happens on every laptop that was asleep.
 */
export function useTopicReset(): void {
  const client = useQueryClient();

  useEffect(() => {
    if (!REALTIME_ENABLED) return undefined;

    return getRealtimeClient().onTopicReset((topic) => {
      if (topic === null) {
        void client.invalidateQueries();
        return;
      }
      const [kind, id] = topic.split(':');
      if (kind === 'conversation' && id !== undefined) {
        const conversationId = asId<ConversationId>(id);
        void client.invalidateQueries({ queryKey: CHAT_KEYS.timeline(conversationId) });
        void client.invalidateQueries({ queryKey: POST_KEYS.feed(conversationId) });
        void client.invalidateQueries({ queryKey: POST_KEYS.pinned(conversationId) });
        return;
      }
      /* Anything else is cheap to re-read wholesale — the sidebar is one call. */
      void client.invalidateQueries({ queryKey: QUERY_KEYS.sidebar() });
      void client.invalidateQueries({ queryKey: QUERY_KEYS.notificationCounts() });
    });
  }, [client]);
}
